import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import AdviceService from '../services/adviceService';
import { useAuth } from '../contexts/AuthContext';
import { AdBanner } from './GoogleAdsense';
import PlanInfo from './PlanInfo';

function AdvicePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { formData } = location.state || {};
  const [advice, setAdvice] = useState(null);
  const [adviceResponse, setAdviceResponse] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quotaError, setQuotaError] = useState(false);

  useEffect(() => {
    if (!formData) {
      navigate('/');
      return;
    }
    generatePersonalizedAdvice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  const generatePersonalizedAdvice = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await AdviceService.generateAdvice({ formData });
      
      // Store the full response including plan and model info
      setAdviceResponse(result);
      
      // APIからの回答を解析して構造化
      const adviceText = result.advice;
      const parsedAdvice = parseAIAdvice(adviceText);
      
      setAdvice(parsedAdvice);
    } catch (err) {
      console.error('アドバイス生成エラー:', err);
      
      if (err.quotaExceeded) {
        setQuotaError(true);
        setError(err.error || '今月は上限までご利用いただきました。来月またご利用いただけます。');
      } else {
        setError(err.message || 'アドバイスの生成に失敗しました。');
        // エラー時はデフォルトのアドバイスを表示
        setAdvice(getDefaultAdvice());
      }
    } finally {
      setIsLoading(false);
    }
  };

  const parseAIAdvice = (adviceText) => {
    // AIの回答を解析して構造化
    const sections = adviceText.split('\n').filter(s => s.trim());
    const advice = {
      sections: []
    };

    let currentSection = null;
    let currentContent = [];

    sections.forEach(line => {
      // セクションヘッダーを検出（【】や数字.で始まる行）
      if (line.match(/^【.*】|^\d+\.|^■|^◆|^▼/)) {
        if (currentSection) {
          advice.sections.push({
            title: currentSection,
            content: currentContent
          });
        }
        currentSection = line;
        currentContent = [];
      } else if (line.trim()) {
        currentContent.push(line.trim());
      }
    });

    // 最後のセクションを追加
    if (currentSection) {
      advice.sections.push({
        title: currentSection,
        content: currentContent
      });
    }

    // セクションが見つからない場合は全体を1つのセクションとして扱う
    if (advice.sections.length === 0) {
      advice.sections.push({
        title: 'アドバイス',
        content: sections
      });
    }

    return advice;
  };

  const getDefaultAdvice = () => {
    return {
      sections: [
        {
          title: '【橋本病の食事管理】',
          content: [
            'ヨウ素の過剰摂取に注意（海藻類は週2-3回程度に）',
            'タンパク質を十分に摂取（魚、鶏肉、豆腐など）',
            '鉄分・ビタミンB群・ビタミンDを意識的に摂取',
            '冷たい飲み物より温かい飲み物を選ぶ'
          ]
        },
        {
          title: '【生活習慣のポイント】',
          content: [
            '規則正しい生活リズムを心がける',
            '適度な運動（ウォーキングなど）',
            'ストレス管理を大切に',
            '十分な睡眠時間の確保'
          ]
        },
        {
          title: '【注意事項】',
          content: [
            '定期的な医療機関での検査を継続',
            '症状の変化を記録',
            '処方薬は医師の指示通りに服用',
            '体調に異変を感じたら早めに受診'
          ]
        }
      ]
    };
  };

  if (!formData) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-3xl mx-auto px-4">
          <div className="bg-blue-50 rounded-lg shadow-md p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
            <h2 className="text-xl font-semibold text-gray-800">
              あなた専用のアドバイスを生成中...
            </h2>
            <p className="text-gray-600 mt-2">
              少々お待ちください
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (quotaError) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-3xl mx-auto px-4">
          <div className="bg-red-50 rounded-lg shadow-md p-8 text-center">
            <svg className="h-12 w-12 text-red-600 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              {error}
            </h2>
            <p className="text-gray-600 mb-6">
              {isAuthenticated ? (
                <>プランをアップグレードして、より多くのアドバイスを受け取りましょう。</>
              ) : (
                <>アカウントを作成して、より多くの機能をご利用ください。</>
              )}
            </p>
            <div className="space-y-2">
              {isAuthenticated ? (
                <Link
                  to="/settings"
                  className="inline-block bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700"
                >
                  プランをアップグレード
                </Link>
              ) : (
                <>
                  <Link
                    to="/register"
                    className="inline-block bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 mr-4"
                  >
                    新規登録
                  </Link>
                  <Link
                    to="/login"
                    className="inline-block bg-gray-200 text-gray-700 px-6 py-3 rounded-md hover:bg-gray-300"
                  >
                    ログイン
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-3xl mx-auto px-4">
        <h1 className="text-3xl font-bold text-gray-800 mb-8 text-center">
          あなたへのパーソナライズドアドバイス
        </h1>
        
        <PlanInfo showModel={true} adviceResponse={adviceResponse} />
        
        <div className="bg-blue-50 rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center mb-4">
            <span className="text-2xl mr-2">👤</span>
            <h2 className="text-xl font-semibold text-gray-800">
              {formData.age} / {formData.gender || '未回答'} の方へ
            </h2>
          </div>
          
          {formData.symptoms.length > 0 && (
            <div className="mt-2">
              <span className="text-sm text-gray-600">現在お悩みの症状：</span>
              <div className="mt-1">
                {formData.symptoms.map((symptom, index) => (
                  <span key={index} className="inline-block bg-white text-blue-800 px-2 py-1 rounded-md text-sm mr-2 mb-2">
                    {symptom}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-700">⚠️ {error}</p>
            <p className="text-sm text-red-600 mt-2">
              以下は一般的なアドバイスです。より詳細な情報は医師にご相談ください。
            </p>
          </div>
        )}

        {advice && advice.sections.map((section, sectionIndex) => (
          <div key={sectionIndex} className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h3 className="text-xl font-semibold text-gray-800 mb-4 text-blue-600">
              {section.title}
            </h3>
            <div className="space-y-2">
              {section.content.map((item, index) => (
                <div key={index} className="flex items-start">
                  <span className="text-green-600 mr-2 mt-1">•</span>
                  <span className="text-gray-700">{item}</span>
                </div>
              ))}
            </div>
          </div>
        ))}

        <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-4 mb-6">
          <p className="text-sm text-gray-700 font-medium text-center">
            ※このアシスタントは医療行為を行うものではありません。気になる症状や治療に関しては、必ず医師にご相談ください。
          </p>
        </div>

        <div className="flex gap-4">
          <button
            onClick={() => navigate('/')}
            className="flex-1 bg-gray-600 text-white py-3 px-4 rounded-md hover:bg-gray-700 transition duration-200 font-medium"
          >
            フォームに戻る
          </button>
          
          <button
            onClick={() => window.print()}
            className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 transition duration-200 font-medium"
          >
            印刷する
          </button>
        </div>
      </div>
      
      {/* AdSense 広告バナー（無料プランのみ） */}
      <AdBanner />
      
    </div>
  );
}

export default AdvicePage;